import { Box } from '@mui/material';
import { useEffect, useRef } from 'react';
import { getAdSenseConfig } from './adsense-config';

interface AdSenseUnitProps {
  /** Per-slot ID created in the AdSense dashboard. */
  readonly slotId: string;
  /**
   * AdSense ad format. Maps to `data-ad-format` on the `<ins>` element:
   *   - `auto` — responsive auto-sized; Google picks the format.
   *   - `rectangle` — square / rectangle, used for in-grid 16:9 slots.
   *   - `horizontal` — horizontal banner, used between content sections.
   */
  readonly format: 'auto' | 'rectangle' | 'horizontal';
  /** Whether AdSense should auto-resize the unit to the parent's width. */
  readonly responsive?: boolean;
  /** Optional aspect-ratio constraint applied to the wrapping `<Box>`. */
  readonly aspectRatio?: string;
  /**
   * Optional `data-ad-layout-key` for advanced fluid layouts. Most
   * slots leave this undefined; exposed so the operator can tune
   * specific units without editing the component.
   */
  readonly layoutKey?: string;
}

/**
 * `<AdSenseUnit>` — renders a single `<ins class="adsbygoogle">`
 * AdSense ad unit and triggers the post-mount push so AdSense fills
 * the slot. Use only when the operator has wired the publisher ID
 * and slot ID per `lib/adsense/adsense-config.ts`. The parent
 * `<AdSlot>` component falls back to the podcast self-promotion
 * placeholder when AdSense is unconfigured.
 *
 * The `aspectRatio` prop pins the wrapper's shape so the AdSense
 * auction sees a stable inventory size on first render. AdSense's
 * responsive display ad chooses the best creative for that aspect
 * ratio.
 *
 * Per AdSense's documentation, the post-mount push call
 * (`(adsbygoogle = window.adsbygoogle || []).push({})`) MUST happen
 * exactly once per slot per page view. The `useEffect` runs once on
 * mount; React's strict-mode double-invoke is filtered by the
 * `pushed` ref so Google sees a single push even in development.
 */
export function AdSenseUnit({
  slotId,
  format,
  responsive = true,
  aspectRatio,
  layoutKey,
}: AdSenseUnitProps): JSX.Element {
  const { client } = getAdSenseConfig();
  const pushed = useRef(false);
  useEffect(() => {
    if (pushed.current) return;
    pushed.current = true;
    try {
      const w = window as unknown as { adsbygoogle?: unknown[] };
      w.adsbygoogle = w.adsbygoogle || [];
      w.adsbygoogle.push({});
    } catch {
      // AdSense loader not present (e.g., adblocker active). The
      // empty slot remains in the DOM; AdSense's policy explicitly
      // permits this — placeholders are not violations.
    }
  }, []);
  return (
    <Box
      sx={{ width: '100%', display: 'block', overflow: 'hidden', aspectRatio }}
    >
      <ins
        className="adsbygoogle"
        style={{ display: 'block', width: '100%', height: '100%' }}
        data-ad-client={client}
        data-ad-slot={slotId}
        data-ad-format={format}
        data-full-width-responsive={responsive ? 'true' : 'false'}
        {...(layoutKey ? { 'data-ad-layout-key': layoutKey } : {})}
      />
    </Box>
  );
}
